import {Component, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {
  ApiLessonFlag,
} from 'src/app/model/rest/rest-model';
import * as LessonsV3 from 'src/app/model/v3/lessons';
import {EMPTY, forkJoin, Observable, of} from 'rxjs';
import {Page, Pageable} from 'src/app/model/rest/base';
import {ManagerColV3RestService} from "../../services/rest/v3/manager-col-v3-rest.service";
import {PeopleV3RestService} from "../../services/rest/v3/people-rest.service";
import {map, switchMap, tap} from "rxjs/operators";
import {ProgressEstimationUtils} from "../../services/utils/v3/progress-estimation";

@Component({
    selector: 'app-manager-lesson-details-page',
    templateUrl: './manager-lesson-details-page.component.html',
    styleUrls: ['./manager-lesson-details-page.component.css'],
    standalone: false
})
export class ManagerLessonDetailsPageComponent implements OnInit {

  _lessonId: number;
  _schoolId: number;
  _studentId: number | null = null;
  _lessonDetails: LessonsV3.LFTeacherFull;
  lessonProgressCommitted: LessonsV3.LessonProgressDetailsDto | null = null;
  studentContext: LessonsV3.ProductContextDetails | null;
  productCourses: LessonsV3.Course[] = [];
  courseContexts: LessonsV3.ColCourseContext[] = [];
  currentCourse: LessonsV3.Course | null = null;
  currentCourseCode: string | null = null;
  _cancelReason: string;
  readonly reasonMaxLength = 250;
  _startDate: string;
  _finishProgress: LessonsV3.LessonCallanProgressDetailsDto = {
    '@type': 'callan',
    start: 0,
    reading: 0,
    nwp: 0,
    headwords: '',
    productVersion: null,
  };
  _finishDate: string;
  _squanderReason: string;
  _lessonDuration: number;
  _newFlagType: string;
  _newFlagDescription: string;
  _privateComment: string;
  _messageToStudent: string;
  studentProgress: LessonsV3.LessonProgressDetails | null = null;
  estimatedProgress: LessonsV3.LessonProgressDetails | null = null;
  flagTypes = Object.values(LessonsV3.LessonFlagType);
  historyPageable: Pageable;
  lastHistoryPage: Page<LessonsV3.LFTeacherFull>;
  history: LessonsV3.LFTeacherFull[] = [];
  hasHistoryNext: boolean;
  teacherPhotoUrl: string | null = null;
  studentPhotoUrl: string | null = null;

  set lessonId(lessonId: number) {
    this._lessonId = lessonId;
    this.loadData();
  }

  set schoolId(schoolId: number) {
    this._schoolId = schoolId;
    this.loadData();
  }

  set lessonDetails(lessonDetails: LessonsV3.LFTeacherFull) {
    this._lessonDetails = lessonDetails;
    this.fillLessonDuration();
    this.fillLessonMessages();
    this.lessonProgressCommitted = (lessonDetails?.progressCommited ?? null) as LessonsV3.LessonProgressDetailsDto | null;
    this.loadLessonPeoplePhotos();
  }

  get lessonDetails() {
    return this._lessonDetails;
  }

  constructor(activatedRoute: ActivatedRoute,
              private managerV3Rest: ManagerColV3RestService,
              private peopleV3Rest: PeopleV3RestService) {
    activatedRoute.paramMap.subscribe( params => this.lessonId = Number(params.get('lessonId')));
    activatedRoute.parent.paramMap.subscribe(params => this._studentId = Number(params.get('studentId')) || null);
    activatedRoute.parent.parent.paramMap.subscribe( params => this.schoolId = Number(params.get('schoolId')));
  }

  // helper function - used in getters / setters
  fillLessonMessages() {
    this._privateComment = this._messageToStudent = null;
    if (!this.lessonDetails || !this.lessonDetails.message) {
      return;
    }
    this._privateComment = this.lessonDetails.message.privateLessonComment;
    this._messageToStudent = this.lessonDetails.message.message;
  }

  fillLessonDuration() {
    const started = this.lessonDetails.lessonMetric.started;
    this._startDate = started
      ? `${started.getFullYear()}-${String(started.getMonth() + 1).padStart(2, '0')}-${String(started.getDate()).padStart(2, '0')}T${String(started.getHours()).padStart(2, '0')}:${String(started.getMinutes()).padStart(2, '0')}`
      : '';
    this._lessonDuration = this.lessonDetails.lessonMetric.duration;
    if (this._lessonDuration) {
      this._lessonDuration /= 1000 * 60;
      this._lessonDuration = Math.round(this._lessonDuration);
    }
  }

  // main load section
  loadData() {
    if (!this._schoolId || !this._lessonId) {
      return;
    }

    this.historyPageable = Pageable.of(0, 10, ['metric.details.plannedStartDate,DESC']);
    this.history = [];

    // the order is important and calls are dependent on it
    this.loadLessonDetails().pipe(
      switchMap(() => this.loadProductCourses()),
      switchMap(() => this.loadStudentContext()),
      switchMap(() => this.loadStudentProgress()),
      switchMap(() => this.estimateNextProgress()),
      switchMap(() => this.loadLessonHistoryPage())
    )
    .subscribe();
  }

  // main data loaders

  loadLessonDetails() {
    if (!this._schoolId || !this._lessonId) {
      return EMPTY;
    }

    const loadFull = (studentId: number | null) =>
      this.managerV3Rest.getLessonDetails(this._schoolId, this._lessonId, 'teacher-full', studentId)
        .pipe(tap((lessonDetails) => this.lessonDetails = lessonDetails as LessonsV3.LFTeacherFull));

    if (this._studentId) {
      return loadFull(this._studentId);
    }

    return this.managerV3Rest.getLessonStudents(this._lessonId).pipe(
      switchMap((students) => loadFull(students?.[0]?.reference?.id ?? null))
    );
  }

  loadProductCourses(): Observable<LessonsV3.Course[]> {
    if (!this.lessonDetails) {
      return EMPTY;
    }

    const productCode = this.getProductCode();
    if (!productCode) {
      return EMPTY;
    }

    return this.managerV3Rest.listProductCourses(this._schoolId, productCode)
    .pipe(
      tap(courses => this.productCourses = courses)
    );
  }

  loadStudentContext() {
    if (!this.lessonDetails || !this.lessonDetails.teacher || !this.lessonDetails.students || this.lessonDetails.students.length !== 1) {
      return EMPTY;
    }

    const productCode = this.getProductCode();
    const studentId = this.lessonDetails.students[0].reference.id;
    if (!productCode || !studentId) {
      return EMPTY;
    }

    return forkJoin({
      context: this.managerV3Rest.getProductContexts(this._schoolId, studentId, productCode).pipe(map(contexts => contexts?.[0] ?? null)),
      courseContexts: this.managerV3Rest.getCourseContexts(this._schoolId, studentId, productCode),
    }).pipe(
      tap(({context, courseContexts}) => {
        this.studentContext = context;
        this.courseContexts = courseContexts ?? [];
        this.updateCurrentCourse();
      })
    );
  }

  loadStudentProgress() {
    if (!this.lessonDetails) {
      return EMPTY;
    }

    return of(this.lessonProgressCommitted).pipe(
      tap((progress) => this.studentProgress = progress),
    );
  }

  estimateNextProgress() {
    const productCode = this.getProductCode();
    if (!this.studentProgress || !productCode) {
      return EMPTY;
    }

    return of(
      ProgressEstimationUtils.estimateNextProgress(
        this.studentProgress,
        productCode,
        this.studentContext?.productVersion ?? this.studentProgress.productVersion ?? null,
      )
    ).pipe(
      tap((progress) => this.estimatedProgress = progress)
    );
  }

  loadLessonHistoryPage(): Observable<Page<LessonsV3.LFTeacherFull>> {
    const studentId = this.lessonDetails?.students?.[0]?.reference?.id;
    if (!studentId) {
      return EMPTY;
    }

    const dateTo = new Date(Date.now() + (1000 * 60 * 60 * 48));
    return this.managerV3Rest.findLessons(
      'col',
      null,
      'teacher-full',
      null,
      studentId,
      null,
      null,
      null,
      null,
      [
        LessonsV3.LessonStatus.Complete,
        LessonsV3.LessonStatus.Booked,
        LessonsV3.LessonStatus.Due,
        LessonsV3.LessonStatus.InvalidatedByStudent,
        LessonsV3.LessonStatus.InvalidatedByTeacher,
      ],
      null,
      dateTo,
      this.historyPageable
    ).pipe(
      map((page) => {
        const content = (page.content ?? [])
          .map((lesson) => lesson as LessonsV3.LFTeacherFull)
          .filter((lesson) => lesson?.id !== this._lessonId);
        const filteredCount = (page.content ?? []).length - content.length;
        return {
          ...page,
          content,
          totalElements: Math.max(0, (page.totalElements ?? 0) - filteredCount),
          numberOfElements: content.length,
          empty: content.length === 0,
        } as Page<LessonsV3.LFTeacherFull>;
      }),
      tap( lessonHistoryPage => {
        this.lastHistoryPage = lessonHistoryPage;
        this.history = this.history.concat(lessonHistoryPage.content ?? []);
        this.hasHistoryNext = !lessonHistoryPage.last;
      })
    );
  }

  // loaders helpers

  private getProductCode(): string | null {
    const courseCode = this.lessonDetails?.course?.code;
    if (!courseCode) {
      return null;
    }
    const separator = courseCode.lastIndexOf('.');
    return separator > 0 ? courseCode.substring(0, separator) : courseCode;
  }

  private updateCurrentCourse() {
    if (!this.courseContexts || this.courseContexts.length === 0) {
      this.currentCourse = null;
      this.currentCourseCode = null;
      return;
    }
    this.currentCourseCode = this.courseContexts.find((context) => context.current)?.course?.code ?? null;
    this.currentCourse = this.productCourses.find((course) => course.code === this.currentCourseCode) ?? null;
  }

  getLessonStatus() {
    return this.lessonDetails?.status ?? null;
  }

  // view accessors

  public mayBook() {
    return this.getLessonStatus() === LessonsV3.LessonStatus.Initializing;
  }

  public mayConfirm() {
    return this.getLessonStatus() === LessonsV3.LessonStatus.Booked;
  }

  public mayCancel() {
    if (this.getLessonStatus() === LessonsV3.LessonStatus.Booked /* ||
    (this.getLessonStatus() === LessonsV3.LessonStatus.Due
      && Dates.diff(new Date(), this.getLessonDate()) > TimeUnits.Hours(12).toMilis()) */
      ) {
        return true;
      }
  }

  public mayStart() {
    return this.getLessonStatus() === LessonsV3.LessonStatus.Due;
  }

  public mayFinish() {
    return this.getLessonStatus() === LessonsV3.LessonStatus.InProgress;
  }

  public maySquander() {
    return this.getLessonStatus() === LessonsV3.LessonStatus.Due && !this.mayCancel();
  }

  public mayUpdateLessonTime() {
    return this.getLessonStatus() === LessonsV3.LessonStatus.Complete;
  }

  hasPrivateComment(lesson: LessonsV3.LFTeacherFull) {
    return lesson
      && lesson.message
      && lesson.message.privateLessonComment
      && lesson.message.privateLessonComment.length > 1;

  }

  getPrivateComment(lesson: LessonsV3.LFTeacherFull) {
    if (!this.hasPrivateComment(lesson)) {
      return '';
    }
    return  lesson.message.privateLessonComment;
  }

  hasComment(lesson: LessonsV3.LFTeacherFull) {
    return lesson
      && lesson.message
      && lesson.message.lessonComment
      && lesson.message.lessonComment.length > 1;
  }

  getComment(lesson: LessonsV3.LFTeacherFull) {
    if (!this.hasComment(lesson)) {
      return '';
    }
    return lesson.message.lessonComment;
  }

  hasStudentMessage() {
    return this.lessonDetails
      && this.lessonDetails.studentMessage
      && this.lessonDetails.studentMessage.length > 1;
  }

  public hasFlags() {
    return !(!this.lessonDetails || !this.lessonDetails.flags || this.lessonDetails.flags.length === 0);
  }

  public getLessonDate() {
    if (!this.lessonDetails || !this.lessonDetails.lessonMetric) {
      return null;
    }
    return this.lessonDetails.lessonMetric.plannedStartDate;
  }

  public getStartDate() {
    if (!this.lessonDetails || !this.lessonDetails.lessonMetric) {
      return null;
    }
    return this.lessonDetails.lessonMetric.started;
  }

  public getFlagTypeName(flag: string) {
    return ApiLessonFlag.getFLagDescription(flag);
  }

  public getPersonName(person: { name?: string; surname?: string } | null) {
    if (!person) {
      return '';
    }
    return [person.name, person.surname].filter((part) => !!part).join(' ');
  }

  public getPersonPhotoUrl(personId: number | null | undefined) {
    if (!personId) {
      return null;
    }
    if (this.teacherPhotoUrl && this.lessonDetails?.teacher?.reference?.person?.id === personId) {
      return this.teacherPhotoUrl;
    }
    if (this.studentPhotoUrl && this.lessonDetails?.students?.[0]?.reference?.person?.id === personId) {
      return this.studentPhotoUrl;
    }
    return null;
  }

  public getLessonColorClass(lesson: LessonsV3.LFTeacherFull) {
    if (!lesson) {
      return '';
    }
    if (lesson.type === LessonsV3.LessonType.Revision || lesson.type === LessonsV3.LessonType.QuickStageRevision) {
      return 'lesson-revision';
    }
    if (lesson.flags?.some((flag) => flag.flagType === LessonsV3.LessonFlagType.StudentNotShow)) {
      return 'lesson-student-no-show';
    }
    if (lesson.flags?.some((flag) => flag.flagType === LessonsV3.LessonFlagType.TeacherNotShow)) {
      return 'lesson-tutor-no-show';
    }
    switch (lesson.status) {
      case LessonsV3.LessonStatus.InvalidatedByTeacher:
        return 'lesson-tutor-no-show';
      case LessonsV3.LessonStatus.Cancel:
        return 'text-muted text-del';
      default:
        return '';
    }
  }

  // lesson actions

  public book() {
    this.managerV3Rest.updateStatus(this._lessonId, LessonsV3.LessonStatus.Booked).pipe(
      switchMap(() => this.loadLessonDetails()),
    ).subscribe();
  }

  public confirm() {
    this.managerV3Rest.updateStatus(this._lessonId, LessonsV3.LessonStatus.Due).pipe(
      switchMap(() => this.loadLessonDetails()),
    ).subscribe();
  }

  public cancel() {
    const teacherId = this.lessonDetails?.teacher?.reference?.id ?? null;
    this.managerV3Rest.updateStatus(
      this._lessonId,
      LessonsV3.LessonStatusAction.Cancel,
      {reason: this._cancelReason},
      null,
      teacherId
    ).pipe(
      switchMap(() => this.loadLessonDetails()),
    ).subscribe();
  }

  public squander() {
    const teacherId = this.lessonDetails?.teacher?.reference?.id ?? null;
    this.managerV3Rest.updateStatus(
      this._lessonId,
      LessonsV3.LessonStatusAction.Invalidate,
      {reason: this._squanderReason},
      null,
      teacherId
    ).pipe(
      switchMap(() => this.loadLessonDetails()),
    ).subscribe();
  }

  public start() {
    this.managerV3Rest.updateStatus(this._lessonId, LessonsV3.LessonStatus.InProgress).pipe(
      switchMap(() => this.loadLessonDetails()),
    ).subscribe();
  }

  public finish() {
    this.managerV3Rest.updateStatus(this._lessonId, LessonsV3.LessonStatus.Complete)
    .pipe(
      switchMap(() => this.managerV3Rest.updateLessonProgress(this._lessonId, this._finishProgress)),
      switchMap(() => this.loadLessonDetails()),
    ).subscribe();
  }

  public updateLessonType() {
    this.managerV3Rest.updateLessonType(this._lessonId, this.lessonDetails.type).pipe(
      switchMap(() => this.loadLessonDetails()),
    ).subscribe();
  }

  public updateProgress() {
    if (!this.lessonProgressCommitted) {
      return;
    }
    this.managerV3Rest.updateLessonProgress(
      this._lessonId,
      this.lessonProgressCommitted
    ).pipe(
      switchMap(() => this.loadLessonDetails()),
      switchMap(() => this.loadStudentProgress())
    ).subscribe();
  }

  public updateLessonTime() {
    const existing = this.lessonDetails?.lessonMetric;
    if (!existing) {
      return;
    }
    const metric: LessonsV3.V3LessonMetric = {
      plannedStartDate: existing.plannedStartDate,
      plannedDuration: existing.plannedDuration,
      timezone: existing.timezone,
      started: this._startDate ? new Date(this._startDate) : existing.started,
      duration: this._lessonDuration * 1000 * 60,
    };
    this.managerV3Rest.updateLessonMetric(this._lessonId, metric)
    .pipe(
      switchMap(() => this.loadLessonDetails())
    ).subscribe();
  }

  public saveFlag() {
    const flag: LessonsV3.LessonFlagDto = {
      flagType: this._newFlagType as LessonsV3.LessonFlagType,
      description: this._newFlagDescription,
    };
    const teacherId = this.lessonDetails?.teacher?.reference?.id ?? null;

    this.managerV3Rest.registerLessonFlag(this._lessonId, flag, null, teacherId)
    .pipe(
      switchMap( () => this.loadLessonDetails())
    ).subscribe(
      () => {
        this._newFlagDescription = null;
        this._newFlagType = null;
      }
    );
  }

  public saveStudentMessage() {
    const studentId = this.lessonDetails?.students?.[0]?.reference?.id;
    if (!studentId) {
      return;
    }
    this.managerV3Rest.postLessonMessageForStudent(
      this._lessonId,
      studentId,
      this._messageToStudent
    )
    .pipe(
      switchMap(() => this.loadLessonDetails())
    ).subscribe();
  }

  public saveComment() {
    this.managerV3Rest.postLessonComment(
      this._lessonId,
      {
        lessonComment: '',
        privateLessonComment: this._privateComment,
        message: '',
      }
    )
      .pipe(
        switchMap(() => this.loadLessonDetails())
    ).subscribe();
  }

  public saveStudentContext() {
    const studentId = this.lessonDetails?.students?.[0]?.reference?.id;
    const productCode = this.getProductCode();
    if (!studentId || !productCode || !this.studentContext) {
      return;
    }
    const context: LessonsV3.ProductContextDetails = {
      nextLessonType: this.studentContext.nextLessonType,
      productVersion: this.studentContext.productVersion,
    };
    this.managerV3Rest.saveStudentProductContext(
      this._schoolId,
      studentId,
      productCode,
      context
    )
      .pipe(
        switchMap(() => this.loadStudentContext())
      )
      .subscribe();
  }

  historyNext() {
    if (!this.hasHistoryNext) { return; }
    this.historyPageable = this.historyPageable.next();
    this.loadLessonHistoryPage().subscribe();
  }

  public getHistoryDate(lesson: LessonsV3.LFTeacherFull) {
    if (!lesson || !lesson.lessonMetric) { return null; }
    if (!lesson.lessonMetric.started ) {
      return lesson.lessonMetric.plannedStartDate;
    }
    return lesson.lessonMetric.started;
  }

  public getHistoryProgress(lesson: LessonsV3.LFTeacherFull) {
    return lesson?.progressCommited as LessonsV3.LessonProgressDetails | null;
  }

  ngOnInit() {
  }

  getInitials(person: { name?: string; surname?: string } | null) {
    if (!person) {
      return '';
    }
    return [person.name, person.surname].filter((part) => !!part).map((part) => part[0]).join('');
  }

  private loadLessonPeoplePhotos() {
    const teacherId = this.lessonDetails?.teacher?.reference?.person?.id;
    const studentId = this.lessonDetails?.students?.[0]?.reference?.person?.id;
    if (!teacherId && !studentId) {
      this.teacherPhotoUrl = null;
      this.studentPhotoUrl = null;
      return;
    }

    const teacherPhoto$ = teacherId ? this.peopleV3Rest.getPhoto(teacherId) : of(null);
    const studentPhoto$ = studentId ? this.peopleV3Rest.getPhoto(studentId) : of(null);

    forkJoin({
      teacher: teacherPhoto$,
      student: studentPhoto$,
    }).subscribe(({teacher, student}) => {
      this.teacherPhotoUrl = teacher?.publicAccessUrl ?? null;
      this.studentPhotoUrl = student?.publicAccessUrl ?? null;
    });
  }

}
